import React, { useEffect, useState } from "react";
import { MotiView } from "moti";
import { View, StyleSheet } from "react-native";

import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import GradingCenterHeader from "../../components/adminDashboard/GradingCenter/GradingCenterHeader";
import GradingCenterMetrics from "../../components/adminDashboard/GradingCenter/GradingCenterMetrics";
import GradingCenterTable from "../../components/adminDashboard/GradingCenter/GradingCenterTable";
import GradingPanel from "../../components/adminDashboard/GradingCenter/GradingPanel/GradingPanel";
import GradingCenterFilters from "../../components/adminDashboard/GradingCenter/GradingCenterFilters";
import { GradingCenterSubmissionsProvider } from "../../contexts/GradingCenterContext";
import GradingCenterTableFooter from "../../components/adminDashboard/GradingCenter/GradingPanel/GradingCenterTableFooter";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { useGetSubmissionExportFileQuery, useRequestSubmissionExportMutation } from "@axio/graphql/generated/graphql";
import useFileFetch from "../../hooks/useFileFetch";
import { DownloadSimpleIcon } from "@phosphor-icons/react";

import { useRoute, useNavigation } from "@react-navigation/native";
import type { RouteProp } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { RootStackParamList } from "../../navigation/navigation.types";
import { GradingCenterModalState } from "../../components/adminDashboard/GradingCenter/types";
import AdminTabNavigator from "../../components/adminDashboard/AdminTabNavigator/AdminTabNavigator";
import FacultyGradebookContainer from "../../containers/adminDashboard/FacultyGradebookContainer";
import { useAuth } from "../../hooks/useAuth";
import { UserRoleUI } from "@axio/auth-guard";
import { useAppConfig } from "../../AppConfigProvider";

const TO_GRADE_TAB = { key: "to-grade", title: "To Grade" } as const;
const GRADEBOOK_TAB = { key: "gradebook", title: "Grade Book" } as const;

const EXPORT_POLLING_INTERVAL_MS = 3000;
function GradingCenterView() {
  const { userInfo } = useAuth();
  const isFaculty = userInfo.userRole === UserRoleUI.FACULTY;
  const { gradebookEnabled } = useAppConfig();
  const tabs = isFaculty && gradebookEnabled ? [TO_GRADE_TAB, GRADEBOOK_TAB] : [TO_GRADE_TAB];
  const [modalState, setModalState] = useState<GradingCenterModalState>(GradingCenterModalState.CLOSED);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [requestSubmissionExport] = useRequestSubmissionExportMutation();
  const {
    data: submissionExportFileData,
    refetch: fetchLatestSubmissionExport,
    startPolling,
    stopPolling,
  } = useGetSubmissionExportFileQuery({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [exportState, setExportState] = useState<{
    isGenerating: boolean;
    fileURL: string;
    fileId: string | null;
  }>({
    isGenerating: false,
    fileURL: "",
    fileId: null,
  });
  const [pendingExportMetadata, setPendingExportMetadata] = useState<{ previousFileId: string | null } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("to-grade");

  const { fetchAndOpenFile } = useFileFetch();

  useEffect(() => {
    const exportResponse = submissionExportFileData?.getSubmissionExportFile;

    if (exportResponse?.success && exportResponse.data) {
      const file = exportResponse.data;

      if (pendingExportMetadata && file.id !== pendingExportMetadata.previousFileId) {
        setExportState({
          isGenerating: false,
          fileURL: file.fileURL ?? "",
          fileId: file.id,
        });
        setPendingExportMetadata(null);
        stopPolling?.();
      } else if (!exportState.isGenerating && !exportState.fileURL) {
        setExportState((prev) => ({
          ...prev,
          fileURL: file.fileURL ?? "",
          fileId: file.id,
        }));
      }
    }
  }, [submissionExportFileData, pendingExportMetadata]);

  /* 👇 deep-link support (ADD THIS, don’t replace anything) */
  const route = useRoute<AdminGradingCenterRouteProp>();
  const submissionId = route.params?.submissionId;

  const handleDownloadExport = async () => {
    if (exportState.fileURL) {
      await fetchAndOpenFile(exportState.fileURL);
    }
  };

  const handleExportSubmission = async () => {
    try {
      if (exportState.isGenerating) {
        return;
      }

      setExportError(null);
      setExportState((prev) => ({ ...prev, isGenerating: true, fileURL: "", fileId: null }));

      const previousFileId = submissionExportFileData?.getSubmissionExportFile?.data?.id ?? null;
      setPendingExportMetadata({ previousFileId });

      const res = await requestSubmissionExport();
      if (!res?.data?.requestSubmissionExport?.success) {
        throw new Error(res?.data?.requestSubmissionExport?.message || "Failed to start submission export");
      }

      await fetchLatestSubmissionExport();
      stopPolling?.();
      startPolling?.(EXPORT_POLLING_INTERVAL_MS);
    } catch (err) {
      console.error("Error starting submission export:", err);
      setExportError(err instanceof Error ? err.message : "Failed to start submission export");
      setExportState((prev) => ({ ...prev, isGenerating: false }));
      setPendingExportMetadata(null);
      stopPolling?.();
    }
  };

  return (
    <AdminLayout>
      <GradingCenterHeader />
      {tabs.length > 1 && (
        <AdminTabNavigator
          tabData={tabs}
          activeTab={activeTab}
          onTabPress={(tab) => {
            setActiveTab(tab);
            setModalState(GradingCenterModalState.CLOSED);
          }}
        />
      )}
      {activeTab === "to-grade" && (
        <GradingCenterSubmissionsProvider>
          <MotiView
            from={{
              opacity: 0,
              translateY: 20,
              scale: 1,
            }}
            animate={{
              opacity: 1,
              translateY: 0,
              scale: 1,
            }}
            delay={10}
            // @ts-expect-error - transition is not typed
            transition={{ type: "timing", duration: 500 }}>
            <GradingCenterMetrics />

            <View style={styles.tableContainer}>
              <View style={styles.tableActionRow}>
                <CustomButton
                  text={exportState.isGenerating ? "Generating..." : "Export Submission"}
                  styleType="primaryLight"
                  size="m"
                  leftIcon={<DownloadSimpleIcon size={24} color="#3E68FE" />}
                  onPress={handleExportSubmission}
                  disabled={exportState.isGenerating}
                  useTranslationText={false}
                  style={styles.exportButton}
                  textStyle={{ color: "#3E68FE", fontWeight: "700", fontSize: 14 }}
                />
                {exportState.fileURL && !exportState.isGenerating ? (
                  <CustomButton
                    text="Download Export"
                    size="s"
                    weight={700}
                    styleType="primaryLight"
                    onPress={handleDownloadExport}
                    useTranslationText={false}
                  />
                ) : null}
              </View>
              {exportError && (
                <CustomText text={exportError} size="xs" useTranslationText={false} style={styles.exportErrorText} />
              )}
              <GradingCenterFilters />
              <GradingCenterTable setModalState={setModalState} />
              <GradingCenterTableFooter />
            </View>

            {/* Action Result Feedback */}
            {actionResult && (
              <View style={styles.feedbackContainer}>
                <CustomText text={actionResult} size="s" style={styles.feedbackText} useTranslationText={false} />
              </View>
            )}

            {/* Error Feedback */}
            {updateError && (
              <View style={[styles.feedbackContainer, styles.errorContainer]}>
                <CustomText text={updateError} size="s" style={styles.errorText} useTranslationText={false} />
              </View>
            )}
          </MotiView>

          {/* Grading Panel - Rendered at root level */}

          <GradingPanel
            modalState={modalState}
            setModalState={setModalState}
            setActionResult={setActionResult}
            setUpdateError={setUpdateError}
          />
        </GradingCenterSubmissionsProvider>
      )}
      {activeTab === "gradebook" && isFaculty && (
        <GradingCenterSubmissionsProvider>
          <FacultyGradebookContainer modalState={modalState} setModalState={setModalState} />
          <GradingPanel
            modalState={modalState}
            setModalState={setModalState}
            setActionResult={setActionResult}
            setUpdateError={setUpdateError}
          />
        </GradingCenterSubmissionsProvider>
      )}
    </AdminLayout>
  );
}

export default GradingCenterView;

const styles = StyleSheet.create({
  feedbackContainer: {
    position: "absolute",
    bottom: 20,
    right: 20,
    backgroundColor: "colors.success",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    shadowColor: "colors.black",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    maxWidth: 300,
  },
  feedbackText: {
    color: "colors.white",
    fontWeight: "500",
  },
  errorContainer: {
    backgroundColor: "colors.error",
  },
  errorText: {
    color: "colors.white",
    fontWeight: "500",
  },
  tableContainer: {
    backgroundColor: "colors.white",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "colors.gray[200]",
  },
  tableActionRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingTop: 16,
    paddingLeft: 16,
    paddingRight: 16,
    height: 64,
    columnGap: 12,
  },
  exportButton: {
    backgroundColor: "colors.white",
    borderWidth: 1,
    borderColor: "colors.gray[500]",
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    shadowColor: "colors.black",
    shadowOpacity: 0.04,
    borderRadius: 8,
  },
  exportErrorText: {
    color: "colors.error",
    marginLeft: 16,
    marginBottom: 4,
  },
});
